import { useMemo, useState } from "react";
import { RiArrowRightLine, RiFileCopyLine, RiPlayLine } from "@remixicon/react";

import scraperApi from "api";

import { Button } from "components/Button";
import { CodeView, CodeViewLanguages } from "components/CodeView";
import DecoratedInputField from "components/InputFieldComponents/DecoratedInputField";
import Select, { SelectOption } from "components/Select";

import { useCopyToClipboard } from "hooks/useCopyToClipboard";

import { CollectorType } from "providers/HostedScrapingProvider";

import { ApiCallConfig, ScrapingMethod, SupportedLanguages } from "routes/dashboard/ApiPlaygroundTypes";
import { useUser } from "routes/dataroutes/UserData";

import { sampleCode } from "components/hosted-scraping/apiPlaygroundTemplates";
import { aiResultsProjectTypes, codeViewPlaceHolderAP, enterInputsPlaceholderAP, isAiResultsBetaActive, sdeDescriptors } from "sdecontent";
import { formatJson, isJson } from "utils/jsonUtils";

import { cx } from "utils";


interface TryItResult {
  status: number;
  statusText: string;
  responseText: string;
}

const DEFAULT_ENDPOINT: CollectorType = "async_chatgpt";
const SCRAPING_METHOD: ScrapingMethod = "structured_data_endpoint";

// Recolours the shared Select button to match the dark code block.
const darkSelectButton = "[&_.select-button]:!rounded [&_.select-button]:!border-white [&_.select-button]:!bg-slate-900 [&_.select-button]:!text-white [&_.select-button_svg]:!text-neutral-400";

// The "New" label is shown until 3 months after launch (July 2026).
const NEW_LABEL_UNTIL = new Date("2026-10-07");
const DOCS_URL = "https://docs.scraperapi.com/structured-data-endpoints/ai-results";

const endpointOptions: SelectOption<CollectorType>[] = sdeDescriptors
  .filter((descriptor) => aiResultsProjectTypes.includes(descriptor.projectType))
  .map((descriptor) => ({ name: descriptor.apiPlayground.dropdownTitle, value: descriptor.projectType, beta: isAiResultsBetaActive() }));

const languageOptions: SelectOption<SupportedLanguages>[] = [
  { name: "cURL", value: "curl" },
  { name: "Python", value: "python" },
  { name: "NodeJS", value: "nodejs" },
  { name: "PHP", value: "php" },
  { name: "Ruby", value: "ruby" },
  { name: "Java", value: "java" },
];

const StepLabel = ({ index, label }: { index: string; label: string }) => (
  <div className="flex items-center gap-[5px]">
    <div className="flex size-[23px] items-center justify-center rounded-full bg-neutral-100 text-xs font-bold text-neutral-900">
      { index }
    </div>
    <span className="text-xs font-bold tracking-[0.5px] text-neutral-900">{ label }</span>
  </div>
);

export default function AiResultsEndpointsWidget({ className }: { className?: string }) {
  const user = useUser();
  const apiKey = user?.apiKey ?? "";
  const showNew = new Date() < NEW_LABEL_UNTIL;

  const [ type, setType ] = useState<CollectorType>(DEFAULT_ENDPOINT);
  const [ input, setInput ] = useState("");
  const [ language, setLanguage ] = useState<SupportedLanguages>("curl");
  const [ tryItResult, setTryItResult ] = useState<TryItResult | undefined>(undefined);
  const [ tryItInProgress, setTryItInProgress ] = useState(false);

  const [ apiKeyCopied, copyApiKey ] = useCopyToClipboard();
  const [ codeCopied, copyCode ] = useCopyToClipboard();

  const hasInput = input.trim().length > 0;

  const generatedCode = useMemo(
    () => (hasInput ? sampleCode(language, SCRAPING_METHOD, type, input, apiKey, {}) ?? "" : ""),
    [ hasInput, language, type, input, apiKey ]
  );

  // Any change to the request invalidates a previous run's result.
  const resetResult = () => setTryItResult(undefined);

  const runTryIt = async () => {
    if (!hasInput || tryItInProgress) {
      return;
    }
    setTryItInProgress(true);
    setTryItResult(undefined);
    try {
      const config: ApiCallConfig = {
        input,
        scrapingMethod: SCRAPING_METHOD,
        config: { type, apiParams: {} },
      };
      const result = await scraperApi.hostedScraping.tryIt(config) as TryItResult;
      setTryItResult(result);
    } catch (error) {
      setTryItResult({
        status: 0,
        statusText: "Request failed",
        responseText: error instanceof Error ? error.message : "Request failed",
      });
    } finally {
      setTryItInProgress(false);
    }
  };

  const codeBody = tryItResult
    ? formatJson(tryItResult.responseText)
    : hasInput
      ? generatedCode
      : codeViewPlaceHolderAP(type);
  const codeLanguage: CodeViewLanguages = tryItResult
    ? (isJson(tryItResult.responseText) ? "json" : "html")
    : hasInput
      ? language
      : "text";
  const canCopyCode = Boolean(tryItResult) || hasInput;

  return (
    <div className={ cx(
      "overflow-hidden rounded border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.07)]",
      className
    ) }>
      <div className="flex items-center gap-2 bg-primary-50 px-5 py-3">
        { showNew && <span className="rounded bg-primary-600 px-2 py-[5px] text-sm font-semibold text-primary-50">New</span> }
        <p className="text-base text-neutral-700">
          <span className="font-semibold">AI results endpoints:</span> Extract structured data from AI-generated results — ChatGPT, Perplexity, Gemini and Google AI Mode now available.
        </p>
        <a
          href={ DOCS_URL }
          target="_blank"
          rel="noreferrer"
          className="ml-auto shrink-0 whitespace-nowrap text-sm font-semibold text-primary-600 hover:underline"
        >
          Read docs
        </a>
      </div>

      <div className="h-px bg-neutral-200" />

      <div className="flex flex-col _1200:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-4 bg-white p-5">
          <StepLabel index="1" label="BUILD" />
          <Select<SelectOption<CollectorType>>
            size="SM"
            value={ endpointOptions.find((option) => option.value === type) ?? endpointOptions[0] }
            options={ endpointOptions }
            onChange={ (option) => { setType(option.value); resetResult(); } }
            locked={ tryItInProgress }
            buttonTestId="aiWidgetEndpoint"
          />
          <DecoratedInputField
            value={ input }
            onChange={ (event) => { setInput(event.target.value); resetResult(); } }
            placeholder={ enterInputsPlaceholderAP(type) }
            disabled={ tryItInProgress }
            className="w-full rounded border border-neutral-300 px-3 py-1.5 text-sm focus:ring-primary-600"
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button
              text="Try it"
              size="SM"
              uppercase
              className="button bg-success-500 text-white hover:bg-success-600"
              icon={ { Icon: RiPlayLine, position: "leading", size: "w-4 h-4" } }
              loading={ tryItInProgress }
              disabled={ !hasInput || tryItInProgress }
              onClick={ runTryIt }
            />
            <Button
              text="Try in playground"
              size="SM"
              uppercase
              className="button button-primary"
              icon={ { Icon: RiArrowRightLine, position: "trailing", size: "w-4 h-4" } }
              href="/apiplayground"
              navigationState={ { scrapingMethod: SCRAPING_METHOD, collectorType: type, language, url: input } }
            />
          </div>
        </div>

        <div className="hidden w-px bg-neutral-200 _1200:block" />

        <div className="flex min-w-0 flex-1 flex-col gap-4 bg-neutral-50 p-5">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <StepLabel index="2" label="INTEGRATE" />
            <div className="flex items-center gap-1 text-[11px] text-neutral-700">
              <span>API key:</span>
              <span className="max-w-[210px] truncate">{ apiKey }</span>
              <button type="button" onClick={ () => copyApiKey(apiKey) } aria-label="Copy API key">
                <RiFileCopyLine className={ cx("size-3", apiKeyCopied ? "text-success-500" : "text-neutral-400") } />
              </button>
            </div>
          </div>

          <div className="rounded bg-slate-900">
            <div className="relative z-20 flex items-center justify-between gap-2 border-b border-[#1e293b] py-2 pl-3.5 pr-2.5">
              <Select<SelectOption<SupportedLanguages>>
                size="SM"
                value={ languageOptions.find((option) => option.value === language) ?? languageOptions[0] }
                options={ languageOptions }
                onChange={ (option) => { setLanguage(option.value); resetResult(); } }
                className="text-nowrap"
                buttonClassName={ `w-[76px] ${darkSelectButton}` }
                buttonTestId="aiWidgetLanguage"
              />
              <Button
                text={ codeCopied ? "Copied" : "Copy" }
                size="SM"
                uppercase
                className="button button-secondary !text-white !border-white"
                icon={ { Icon: RiFileCopyLine, position: "leading", size: "w-4 h-4" } }
                disabled={ !canCopyCode }
                onClick={ () => copyCode(codeBody) }
              />
            </div>

            <div className="overflow-hidden rounded-b">
              { tryItResult && (
                <div className={ cx(
                  "px-3.5 pt-2 text-xs font-semibold",
                  tryItResult.status >= 200 && tryItResult.status < 300 ? "text-success-500" : "text-error-500"
                ) }>
                  { tryItResult.status > 0 ? `${tryItResult.status} ` : "" }{ tryItResult.statusText }
                </div>
              ) }

              <CodeView
                content={ codeBody }
                language={ codeLanguage }
                placeholder=""
                height="140px"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
